import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import { useTranslation } from 'react-i18next';
import { dsV2 } from '@bloomays-lib/ui.shared';

export type Verbatim = { from: 'client' | 'bloomer'; text: string; author: string; date?: string };

const VbArrow = ({
  children,
  onClick,
  ariaLabel,
}: {
  children: React.ReactNode;
  onClick: () => void;
  ariaLabel: string;
}) => (
  <Box
    component="button"
    type="button"
    aria-label={ariaLabel}
    onClick={onClick}
    sx={{
      width: 24,
      height: 24,
      borderRadius: '6px',
      border: `1px solid ${dsV2.color.border}`,
      background: dsV2.color.blanc,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: dsV2.color.t3,
      transition: 'all .12s',
      '&:hover': { background: dsV2.color.surface2, color: dsV2.color.noir },
    }}
  >
    {children}
  </Box>
);

/** Slider de verbatims satisfaction (`.vb-slider`) : badge source + flèches/points + citation. */
export const SatisfactionComments = ({ comments }: { comments: Verbatim[] }) => {
  const { t } = useTranslation(['missionDashboard']);
  const [index, setIndex] = React.useState(0);
  if (!comments || comments.length === 0) return null;
  const idx = Math.min(index, comments.length - 1);
  const current = comments[idx];
  const isClient = current.from === 'client';
  const go = (delta: number) => setIndex((x) => (((x + delta) % comments.length) + comments.length) % comments.length);

  return (
    <Box sx={{ marginTop: '12px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          marginBottom: '8px',
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            padding: '3px 9px',
            borderRadius: '100px',
            background: isClient ? dsV2.color.violetDim : dsV2.color.bleuDim,
            color: isClient ? dsV2.color.violet : dsV2.color.bleu,
          }}
        >
          {isClient ? <ApartmentRoundedIcon sx={{ fontSize: 12 }} /> : <PersonRoundedIcon sx={{ fontSize: 12 }} />}
          {isClient
            ? t('verbatim.client', { ns: 'missionDashboard' })
            : t('verbatim.bloomer', { ns: 'missionDashboard' })}
        </Box>
        {comments.length > 1 ? (
          <Stack direction="row" spacing="8px" alignItems="center">
            <VbArrow onClick={() => go(-1)} ariaLabel="Précédent">
              <ChevronLeftRoundedIcon sx={{ fontSize: 16 }} />
            </VbArrow>
            <Stack direction="row" spacing="5px">
              {comments.map((_, k) => (
                <Box
                  key={k}
                  onClick={() => setIndex(k)}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    cursor: 'pointer',
                    background: k === idx ? dsV2.color.bleu : dsV2.color.border,
                  }}
                />
              ))}
            </Stack>
            <VbArrow onClick={() => go(1)} ariaLabel="Suivant">
              <ChevronRightRoundedIcon sx={{ fontSize: 16 }} />
            </VbArrow>
          </Stack>
        ) : null}
      </Box>
      <Box
        sx={{
          padding: '11px 14px',
          background: dsV2.color.surface2,
          borderRadius: dsV2.radius.sm,
          border: `1px solid ${dsV2.color.border}`,
          borderLeft: `3px solid ${dsV2.color.bleu}`,
        }}
      >
        <Box
          component="p"
          sx={{ fontSize: 12, color: dsV2.color.t2, fontStyle: 'italic', lineHeight: 1.6, marginBottom: '5px' }}
        >
          "{current.text}"
        </Box>
        <Box component="cite" sx={{ fontSize: 11, color: dsV2.color.t4, fontStyle: 'normal' }}>
          — {current.author}
          {current.date ? ` · ${current.date}` : ''}
        </Box>
      </Box>
    </Box>
  );
};
